
import { CommonModule, HashLocationStrategy, LocationStrategy } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { NgModule, ModuleWithProviders, APP_INITIALIZER } from '@angular/core';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFireAuthModule } from '@angular/fire/compat/auth';
import { AngularFireDatabaseModule } from '@angular/fire/compat/database';
import { AngularFireMessagingModule } from '@angular/fire/compat/messaging';
import { AngularFireStorageModule } from '@angular/fire/compat/storage';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { NgbModule, NgbTooltipConfig  } from '@ng-bootstrap/ng-bootstrap';
import { StoreModule } from '@ngrx/store';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
// import { PushNotificationsModule } from 'ng-push';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { PerfectScrollbarConfigInterface, PerfectScrollbarModule, PERFECT_SCROLLBAR_CONFIG } from 'ngx-perfect-scrollbar';
import { ToastrModule } from 'ngx-toastr';
import { environment } from '../environments/environment';
import { Approutes } from './app-routing.module';
import { AppComponent } from './app.component';
import { CommonComponentsModule } from './common-components/common-components.module';
import { InAppCampaignModalModule } from './common-components/in-app-campaign-modal/in-app-campaign-modal.module';
import { reducers } from './component/store/reducers';
import { HttpInterceptorProviders } from './interceptor';
import { BlankComponent } from './layouts/blank/blank.component';
import { FullComponent } from './layouts/full/full.component';
import { Init } from './middlewares/init';
import { IsAuthenticated } from './middlewares/is-authenticated';
import { ProprietorScope } from './middlewares/proprietor-scope';
import { Roles } from './middlewares/roles';
import { SuperAdminRol } from './middlewares/superAdminRol';
import { SuperAdminMunilyPayRol } from './middlewares/superAdminMunilyPayRol';
import { MacroAdminRol } from './middlewares/macroAdminRol';
//SERVICES
import { AreaService } from './services/area.service';
import { AuthService } from './services/auth.service';
import { BuildingService } from './services/building.service';
import { ChatService } from './services/chat.service';
import { ChatV2Service } from './services/chatv2.service';
import { CommonService } from './services/common.service';
import { CountryService } from './services/country.service';
import { DataService } from './services/data.service';
import { FinanceService } from './services/finance.service';
import { InvitationService } from './services/invitation.service';
import { KpiService } from './services/kpi.service';
import { MessagingService } from './services/messaging.service';
import { NoticeService } from './services/notice.service';
import { ReservationService } from './services/reservation.service';
import { UserService } from './services/user.service';
import { PublicService } from './services/public.service';
//COMPONENTS
import { Constants } from './shared/constants/constants';
import { ErrorsDashboard } from './shared/constants/errors.dashboard';
import { Status } from './shared/constants/status';
import { NavigationComponent } from './shared/header-navigation/navigation.component';
import { NavigationV2Component } from './shared/header-navigation/navigation-v2/navigation-v2.component';
import { NoticesComponent } from './shared/notices/notices.component';
import { ProfileComponent } from './shared/profile/profile.component';
import { SidebarComponent } from './shared/sidebar/sidebar.component';
import { SidebarV2Component } from './shared/sidebar/sidebar-v2/sidebar-v2.component';
import { MenuShellComponent } from './shared/sidebar/menu-shell/menu-shell.component';
import { SpinnerComponent } from './shared/spinner.component';
// import {ChangePasswordComponent} from './shared/change-password/change-password.component';
import { SharedModule } from './shared/shared.module';
import { AmplitudeService } from './services/amplitude.service';
import { GoogleMapsService } from './services/google-maps.service';
import { initHostPreLoginWhiteLabel } from './shared/constants/host-pre-login.initializer';
import { WhiteLabelService } from './services/white-label.service';
import { SupportWhatsappService } from './services/support-whatsapp.service';
import { LayoutMacroCommunityComponent } from './layouts/macro-community/macro-community.component';
import { SidebarMacroCommunityComponent } from './shared/sidebar-macro-community/sidebar-macro-community.component';
import { WhatsappBubbleComponent } from './shared/whatsapp-bubble/whatsapp-bubble.component';
// Angular Material imports
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatButtonModule } from '@angular/material/button';

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
  suppressScrollX: true,
  wheelSpeed: 2,
  wheelPropagation: true,
  minScrollbarLength: 20,
};

export function httpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, '../assets/i18n/', '.json');
  // return new TranslateHttpLoader(http);
}

@NgModule({
  declarations: [
    AppComponent,
    SpinnerComponent,
    FullComponent,
    BlankComponent,
    NavigationComponent,
    NavigationV2Component,
    SidebarComponent,
    SidebarV2Component,
    MenuShellComponent,
    // ChangePasswordComponent,
    ProfileComponent,
    NoticesComponent,
    LayoutMacroCommunityComponent,
    SidebarMacroCommunityComponent,
    WhatsappBubbleComponent,
  ],
  imports: [
    SharedModule,
    CommonComponentsModule,
    InAppCampaignModalModule,
    CommonModule,
    BrowserModule,
    BrowserAnimationsModule,
    StoreModule.forRoot(reducers),
    FormsModule,
    ReactiveFormsModule,
    NgbModule,
    RouterModule.forRoot(Approutes, { useHash: false, relativeLinkResolution: 'legacy' }),
    PerfectScrollbarModule,
    AngularFireModule.initializeApp(environment.firebase),
    AngularFireAuthModule,
    AngularFireDatabaseModule,
    AngularFireStorageModule,
    AngularFireMessagingModule,
    MatIconModule,
    MatMenuModule,
    MatDividerModule,
    MatSidenavModule,
    MatListModule,
    MatButtonModule,
    InfiniteScrollModule,
    HttpClientModule,
    ToastrModule.forRoot({
      timeOut: 10000,
      positionClass: 'toast-bottom-right',
      preventDuplicates: true
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: httpLoaderFactory,
        deps: [HttpClient, HttpClientModule],
      }
    })
  ],
  providers: [
    /** Interceptors */
    HttpInterceptorProviders,

    IsAuthenticated,
    Roles,
    ProprietorScope,
    SuperAdminRol,
    MacroAdminRol,
    SuperAdminMunilyPayRol,
    Init,
    DataService,
    AuthService,
    CommonService,
    UserService,
    PublicService,
    NoticeService,
    AreaService,
    BuildingService,
    ReservationService,
    FinanceService,
    Constants,
    Status,
    MessagingService,
    ChatService,
    ChatV2Service,
    InvitationService,
    CountryService,
    ErrorsDashboard,
    AmplitudeService,
    WhiteLabelService,
    SupportWhatsappService,
    NgbTooltipConfig ,
    {
      provide: PERFECT_SCROLLBAR_CONFIG,
      useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG,
    },
    {
      provide: LocationStrategy,
      useClass: HashLocationStrategy,
    },
    KpiService,
    {
      provide: APP_INITIALIZER,
      useFactory: initHostPreLoginWhiteLabel,
      deps: [WhiteLabelService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (gm: GoogleMapsService) => () => gm.loadGoogleMaps().catch(() => Promise.resolve()),
      deps: [GoogleMapsService],
      multi: true,
    },
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  static forRoot(): ModuleWithProviders<AppModule> {
    return {
      ngModule: AppModule,
    };
  }
}
